import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { useInstallPrompt } from "./useInstallPrompt";

/** Wait before surfacing the prompt so it never nags on first paint. */
export const INSTALL_PROMPT_DELAY_MS = 2500;

/**
 * Install prompt chrome (issue #78).
 *
 * Bottom toast-style, consistent with `PwaUpdatePrompt`. Renders only
 * when the platform can actually install:
 *   - Chrome/Android (`beforeinstallprompt` captured) → "Install" CTA
 *     that calls the stashed `prompt()`.
 *   - iOS Safari → the manual "Add to Home Screen" steps.
 * Nothing renders when standalone/installed, recently dismissed, or on
 * unsupported browsers.
 */
export function InstallPrompt() {
  const { t } = useTranslation();
  const { status, install, dismiss } = useInstallPrompt();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setReady(true), INSTALL_PROMPT_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);

  if (!ready || (status !== "eligible" && status !== "ios")) return null;

  const isIos = status === "ios";

  return (
    <div
      role="dialog"
      aria-label={t("pwa.installTitle")}
      data-testid="install-prompt"
      style={{
        position: "fixed",
        left: 20,
        right: 20,
        bottom: 92,
        zIndex: 70,
        background: "var(--clay-900)",
        color: "#fff",
        borderRadius: "var(--radius-md)",
        padding: "14px 16px",
        boxShadow: "var(--shadow-lg)",
        font: "var(--fw-medium) var(--fs-sm)/1.3 var(--font-body)",
      }}
    >
      {isIos ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: 999,
                background: "var(--brand)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "none",
              }}
            >
              <Icon name="share" size={15} color="#fff" />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>{t("pwa.iosTitle")}</span>
          </div>
          <span
            style={{
              font: "var(--fw-regular) var(--fs-xs)/1.4 var(--font-body)",
              opacity: 0.85,
            }}
          >
            {t("pwa.iosSteps")}
          </span>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={dismiss}
              data-testid="install-prompt-dismiss"
              style={pillButtonStyle}
            >
              {t("pwa.iosDismiss")}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ flex: 1, minWidth: 0 }}>{t("pwa.installTitle")}</span>
          <span style={{ display: "flex", gap: 8, flex: "none" }}>
            <button
              type="button"
              onClick={install}
              data-testid="install-prompt-install"
              style={{ ...pillButtonStyle, background: "var(--brand)" }}
            >
              {t("pwa.installCta")}
            </button>
            <button
              type="button"
              onClick={dismiss}
              data-testid="install-prompt-later"
              style={{
                ...pillButtonStyle,
                border: "1px solid rgba(255,255,255,0.25)",
                background: "transparent",
              }}
            >
              {t("pwa.installLater")}
            </button>
          </span>
        </div>
      )}
    </div>
  );
}

const pillButtonStyle: React.CSSProperties = {
  height: 32,
  padding: "0 14px",
  borderRadius: "var(--radius-pill)",
  border: "none",
  color: "#fff",
  font: "var(--fw-semibold) var(--fs-xs)/1 var(--font-body)",
  cursor: "pointer",
};
